/** A capture interval must belong to one recorded sensor view. Unknown inputs
 * can extend a possible interval, but confirmed failures always break it. */
export function captureWindow(points, target, accepts) {
  let run = 0, count = 0, end = -1;
  for (const [i, point] of points.entries()) {
    run = accepts(point) ? run + 1 : 0;
    if (run > count) { count = run; end = i; }
  }
  const duration = Math.max(0, count - 1) * (target.length || 0) / Math.max(1, points.length - 1) / Math.max(.01, target.speed ?? 1.4);
  return { duration, count, start: end < 0 ? -1 : end - count + 1, end, met: (target.minDuration || 0) > 0 ? duration + 1e-9 >= target.minDuration : count > 0 };
}

export function summarizeCaptureChecks(checks, needed = 1) {
  const count = accepts => new Set(checks.filter(accepts).map(c => JSON.stringify([c.floorId, c.cameraId]))).size;
  const passingCameras = count(c => c.status === 'met');
  const possibleCameras = count(c => c.status !== 'shortfall');
  return { passingCameras, possibleCameras, status: passingCameras >= needed ? 'met' : possibleCameras >= needed ? 'unknown' : 'shortfall' };
}

export const captureStatusLabel = status => ({ met: 'Configured criteria met', shortfall: 'Shortfall', unknown: 'Needs information', optional: 'No required conditions' })[status] || 'Needs information';

/** Independent checks keep an omitted criterion from looking like a pass. */
export function captureCheckStates(capture) {
  const best = capture?.best;
  const motion = best?.exposureIssue ? 'shortfall' : !best || best.motion === 'unchecked' || !(capture?.maxBlurPx > 0) ? 'unchecked' : best.motion || 'unknown';
  return [
    { key: 'geometry', label: 'View geometry', status: best ? best.geometryMet ? 'met' : 'shortfall' : 'unknown' },
    { key: 'light', label: 'Target light', status: best?.lighting || capture?.lighting || 'unknown' },
    { key: 'motion', label: 'Motion blur', status: motion },
    { key: 'image', label: 'Camera image quality', status: 'unchecked' }
  ];
}
export const checkStateLabel = status => ({ met: 'Meets criterion', shortfall: 'Below criterion', unknown: 'Needs information', unchecked: 'Not assessed' })[status] || 'Needs information';
export const captureDisplayName = c => c.kind === 'task' ? `${c.name} · ${c.direction || 'forward'} · lane ${(c.lane ?? 0) + 1}` : c.name;

export function captureExplanation(capture) {
  if (!capture) return 'This target has not been assessed in this condition.';
  if (capture.status === 'met') return captureCheckStates(capture).find(c => c.key === 'motion').status === 'unchecked'
    ? 'Entered view and light criteria are met. Motion blur has not been assessed.'
    : 'Entered view, light and motion criteria are met. Camera image quality remains unassessed.';
  if (!capture.best) return 'No camera is available to check this target.';
  if (capture.passingCameras > 0) return `${capture.passingCameras} of ${capture.needed} required camera views meet the checks. Review the remaining views.`;
  const best = capture.best;
  if (capture.status === 'unknown') return best.points.some(p => p.status === 'unknown' && p.lighting === 'unknown')
    ? 'Light on the usable capture interval is uncertain. Review the missing lighting inputs.'
    : 'Motion cannot be assessed for the usable capture interval. Review the camera exposure and recorded optical profile.';
  if (!best.geometryMet) return best.geometryReason;
  if (best.exposureIssue) return best.exposureIssue + '. Review the camera settings.';
  if (capture.lighting === 'shortfall') return 'Insufficient light on the target. Move, aim or add a light facing it.';
  if (best.motion === 'shortfall') return 'Motion exceeds the blur limit. Review exposure; a shorter exposure needs more light.';
  if (capture.status === 'shortfall') return 'No camera provides the required continuous interval of view, light and motion checks.';
  return 'Review the camera view, lighting inputs and exposure.';
}

/** Compare identical floor/target/direction IDs, never aggregate an optional
 * success into a required-condition verdict. Missing cells remain unknown. */
export function compareCaptureConditions(result) {
  const conditions = result?.scenarios || [], targets = new Map();
  for (const condition of conditions) for (const capture of condition.captures) {
    const key = JSON.stringify([capture.floorId, capture.kind, capture.id]);
    if (!targets.has(key)) targets.set(key, { key, name: capture.name, floorId: capture.floorId, floorName: capture.floorName, kind: capture.kind, cells: new Map() });
    targets.get(key).cells.set(condition.id, capture);
  }
  return [...targets.values()].map(target => {
    const cells = conditions.map(condition => ({ conditionId: condition.id, required: condition.required !== false, capture: target.cells.get(condition.id) || null }));
    const required = conditions.filter(c => c.required !== false).map(c => target.cells.get(c.id)?.status || 'unknown');
    const status = !required.length ? 'optional' : required.includes('shortfall') ? 'shortfall' : required.includes('unknown') ? 'unknown' : 'met';
    return { ...target, cells, status };
  }).sort((a, b) => ({ shortfall: 0, unknown: 1, met: 2, optional: 3 })[a.status] - ({ shortfall: 0, unknown: 1, met: 2, optional: 3 })[b.status] || a.floorName.localeCompare(b.floorName) || a.name.localeCompare(b.name));
}

export function groupCaptureConditions(result) {
  const groups = new Map(), rank = { shortfall: 0, unknown: 1, met: 2, optional: 3 };
  for (const row of compareCaptureConditions(result)) {
    const capture = row.cells.find(c => c.capture)?.capture;
    const key = JSON.stringify([row.floorId, row.kind, capture?.taskId || capture?.id]);
    if (!groups.has(key)) groups.set(key, { key, name: row.name, floorName: row.floorName, rows: [], status: row.status });
    const group = groups.get(key); group.rows.push(row);
    if (rank[row.status] < rank[group.status]) group.status = row.status;
  }
  return [...groups.values()];
}
